import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminService, type AdminUser } from '@/services/adminService';
import { useAdminApi } from '@/hooks/useAdminApi';

export default function AdminPendingPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: 'approve' | 'delete'; userId: string; userName: string } | null>(null);
  const { handleError } = useAdminApi();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getPendingUsers();
      setUsers(data);
    } catch (err) {
      handleError(err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleApprove = async (userId: string) => {
    setProcessing(userId);
    try {
      await adminService.approveUser(userId);
      setConfirmAction(null);
      await fetchUsers();
    } catch (err) {
      handleError(err);
    } finally {
      setProcessing(null);
    }
  };

  const handleDelete = async (userId: string) => {
    setProcessing(userId);
    try {
      await adminService.deleteUser(userId);
      setConfirmAction(null);
      await fetchUsers();
    } catch (err) {
      handleError(err);
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background-50">
      {/* Header */}
      <header className="px-4 pt-12 pb-6">
        <div className="flex items-center justify-between">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-foreground-700 mb-2 cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-s-line"></i>
              대시보드
            </Link>
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mt-1 leading-tight">
              가입 승인 관리
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              {users.length}명의 승인 대기자가 있어요
            </p>
          </div>
        </div>
      </header>

      {/* User List */}
      <section className="px-4 pb-8">
        {users.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-50 flex items-center justify-center">
              <i className="ri-check-double-line text-xl text-emerald-500"></i>
            </div>
            <p className="text-sm text-foreground-500">모든 가입 신청이 처리되었습니다</p>
          </div>
        ) : (
          <div className="space-y-3">
            {users.map((u) => (
              <div key={u.id} className="bg-white rounded-2xl p-4">
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div className="w-11 h-11 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-foreground-600">
                      {u.name.charAt(0)}
                    </span>
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground-900">{u.name}</p>
                    <p className="text-xs text-foreground-400 truncate">{u.email}</p>
                    <p className="text-[10px] text-foreground-400 mt-1">
                      가입 신청: {u.joinedAt}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => setConfirmAction({ type: 'approve', userId: u.id, userName: u.name })}
                    disabled={processing === u.id}
                    className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {processing === u.id ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <i className="ri-check-line"></i>
                    )}
                    승인
                  </button>
                  <button
                    onClick={() => setConfirmAction({ type: 'delete', userId: u.id, userName: u.name })}
                    disabled={processing === u.id}
                    className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-rose-50 text-rose-600 text-sm font-medium hover:bg-rose-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    <i className="ri-close-line"></i>
                    거절
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Confirm Modal */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-lg animate-in fade-in zoom-in">
            <h3 className="text-base font-bold text-foreground-950 mb-2">
              {confirmAction.type === 'approve' ? '가입 승인 확인' : '가입 거절 확인'}
            </h3>
            <p className="text-sm text-foreground-500 mb-6">
              <strong>{confirmAction.userName}</strong>님의 가입을{' '}
              {confirmAction.type === 'approve' ? '승인할까요?' : '거절할까요? 이 작업은 되돌릴 수 없습니다.'}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmAction(null)}
                className="flex-1 px-4 py-2.5 rounded-xl bg-secondary-100 text-sm font-medium text-foreground-700 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={() => {
                  if (confirmAction.type === 'approve') {
                    handleApprove(confirmAction.userId);
                  } else {
                    handleDelete(confirmAction.userId);
                  }
                }}
                className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-colors cursor-pointer whitespace-nowrap ${
                  confirmAction.type === 'approve'
                    ? 'bg-emerald-500 hover:bg-emerald-600'
                    : 'bg-rose-500 hover:bg-rose-600'
                }`}
              >
                {confirmAction.type === 'approve' ? '승인하기' : '거절하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer spacing */}
      <div className="py-4"></div>
    </div>
  );
}